import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { classifyDocument, classifyDocumentEnhanced, uploadDocument, classifyParagraphs, uploadAndClassifyParagraphs, formatClassifiedDocument } from '../services/api';
import FileUpload from '../components/FileUpload';
import ClassificationBadge from '../components/ClassificationBadge';
import StructuredClassificationDetails from '../components/StructuredClassificationDetails';
import PortionMarkedDocument from '../components/PortionMarkedDocument';
import SecurityShieldLoader from '../components/SecretSquirrelLoader';
import AuthorityReviewFlag from '../components/AuthorityReviewFlag';
import { DocumentIcon, ClockIcon, CheckCircleIcon, ExclamationCircleIcon, ScissorsIcon } from '@heroicons/react/24/outline';

export default function ClassifyDocument() {
  const navigate = useNavigate();
  const [textInput, setTextInput] = useState('');
  const useLegalPolicies = true; // Always use legal policies for legal domain
  const [useParagraphMode, setUseParagraphMode] = useState(true);
  const [useEnhancement, setUseEnhancement] = useState(true);
  const useSemanticRetrieval = true; // Always enabled
  const [useLlmSegmentation, setUseLlmSegmentation] = useState(false);
  const [classificationResult, setClassificationResult] = useState(null);
  const [paragraphResult, setParagraphResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [activeTab, setActiveTab] = useState('text'); // 'text', 'file', or 'formatted'
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const classifyMutation = useMutation({
    mutationFn: ({ text, options }) => {
      return classifyDocumentEnhanced(text, { ...options, useLegalPolicies });
    },
    onMutate: () => {
      // Clear previous results and start loading immediately
      setClassificationResult(null);
      setParagraphResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(10);
      setLoadingMessage('Analyzing document content...');
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 500);
      
      // Store interval for cleanup
      window.classifyProgressInterval = progressInterval;
      
      // Update messages over time
      setTimeout(() => setLoadingMessage('Retrieving legal policies...'), 1000);
      setTimeout(() => setLoadingMessage('Applying privilege analysis...'), 2000);
      setTimeout(() => setLoadingMessage('Finalizing legal assessment...'), 3000);
    },
    onSuccess: (data) => {
      // Clean up progress interval
      if (window.classifyProgressInterval) {
        clearInterval(window.classifyProgressInterval);
        window.classifyProgressInterval = null;
      }
      
      setLoadingProgress(100);
      // Minimum loading time of 2 seconds to ensure animation is visible
      setTimeout(() => {
        setClassificationResult(data);
        setLoadingProgress(0); // Reset progress
        setLoadingMessage('Processing...'); // Reset message
        setIsLocalLoading(false);
      }, 2000);
    },
    onError: () => {
      // Clean up progress interval
      if (window.classifyProgressInterval) {
        clearInterval(window.classifyProgressInterval);
        window.classifyProgressInterval = null;
      }
      
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const paragraphMutation = useMutation({
    mutationFn: ({ text, options }) => {
      return classifyParagraphs(text, { ...options, useLegalPolicies });
    },
    onMutate: () => {
      // Clear previous results and start loading immediately
      setClassificationResult(null);
      setParagraphResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(5);
      setLoadingMessage('Breaking document into paragraphs...');
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 10;
        });
      }, 800);
      
      // Store interval for cleanup
      window.paragraphProgressInterval = progressInterval;
      
      // Update messages over time
      setTimeout(() => setLoadingMessage('Analyzing privilege for each paragraph...'), 1500);
      setTimeout(() => setLoadingMessage('Determining overall privilege status...'), 3000);
    },
    onSuccess: (data) => {
      // Clean up progress interval
      if (window.paragraphProgressInterval) {
        clearInterval(window.paragraphProgressInterval);
        window.paragraphProgressInterval = null;
      }
      
      setLoadingProgress(100);
      // Minimum loading time of 2 seconds to ensure animation is visible
      setTimeout(() => {
        setParagraphResult(data);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 2000);
    },
    onError: () => {
      // Clean up progress interval
      if (window.paragraphProgressInterval) {
        clearInterval(window.paragraphProgressInterval);
        window.paragraphProgressInterval = null;
      }
      
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const uploadMutation = useMutation({
    mutationFn: (file) => {
      // Always use the enhanced upload and classify endpoint
      return uploadAndClassifyParagraphs(file, {
        useLegalPolicies,
        chunkSize: useParagraphMode ? 1500 : 2000,
        useLlmSegmentation
      });
    },
    onMutate: () => {
      // Clear previous results and start loading immediately
      setUploadResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(15);
      setLoadingMessage('Uploading document...');
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 12;
        });
      }, 600);
      
      // Store interval for cleanup
      window.uploadProgressInterval = progressInterval;
      
      // Update messages over time
      setTimeout(() => setLoadingMessage('Extracting text from document...'), 1000);
      setTimeout(() => setLoadingMessage('Running classification analysis...'), 2500);
    },
    onSuccess: (data) => {
      // Clean up progress interval
      if (window.uploadProgressInterval) {
        clearInterval(window.uploadProgressInterval);
        window.uploadProgressInterval = null;
      }
      
      setLoadingProgress(100);
      // Minimum loading time of 2 seconds to ensure animation is visible
      setTimeout(() => {
        // Always treat as paragraph result since we're using enhanced endpoint
        setParagraphResult(data);
        setUploadResult(null);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 2000);
    },
    onError: () => {
      // Clean up progress interval
      if (window.uploadProgressInterval) {
        clearInterval(window.uploadProgressInterval);
        window.uploadProgressInterval = null;
      }
      
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const formattedDocumentMutation = useMutation({
    mutationFn: ({ file, classificationResults }) => {
      return formatClassifiedDocument(file, classificationResults);
    },
    onMutate: () => {
      setIsLocalLoading(true);
      setLoadingProgress(10);
      setLoadingMessage('Generating formatted document...');
    },
    onSuccess: (response) => {
      setLoadingProgress(100);
      setLoadingMessage('Download ready!');
      
      // Create download link
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'classified_document';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setTimeout(() => {
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 1000);
    },
    onError: () => {
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const handleTextClassification = async (e) => {
    e.preventDefault();
    if (!textInput.trim()) return;
    
    setClassificationResult(null);
    setParagraphResult(null);
    
    if (useParagraphMode) {
      paragraphMutation.mutate({
        text: textInput,
        options: { 
          useLegalPolicies,
          useHyde: false, // Disable enhancement for paragraph mode for performance
          useSemanticRetrieval 
        }
      });
    } else {
      classifyMutation.mutate({
        text: textInput,
        options: { 
          useLegalPolicies,
          useHyde: useEnhancement,
          useSemanticRetrieval 
        }
      });
    }
  };

  const handleFileUpload = async (file) => {
    setUploadResult(null);
    uploadMutation.mutate(file);
  };

  const handleTearlineRedirect = () => {
    // Store the current text in sessionStorage so it can be used in Tearline
    if (textInput.trim()) {
      sessionStorage.setItem('tearlineText', textInput);
      navigate('/tearline');
    }
  };

  const renderResult = (result, type = 'classification') => {
    if (!result) return null;

    return (
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              {type === 'upload' ? 'Document Uploaded & Classified' : 'Classification Result'}
            </h3>
          </div>
          
          {/* Tearline Button */}
          {textInput.trim() && type === 'classification' && (
            <button
              onClick={handleTearlineRedirect}
              className="inline-flex items-center px-3 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ScissorsIcon className="w-4 h-4 mr-2" />
              Tearline
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Classification Level
            </label>
            <div className="space-y-2">
              <ClassificationBadge 
                classification={result.classification}
                fullClassification={result.full_classification}
                caveats={result.caveats}
                releasability={result.releasability}
                confidence={result.confidence}
              />
              <AuthorityReviewFlag 
                reasoning={result.raw_response}
                contradictions={result.contradictions}
                variant="compact" 
              />
            </div>
          </div>
          
          {result.extraction_method && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extraction Method
              </label>
              <span className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-blue-100 text-blue-800">
                {result.extraction_method}
              </span>
            </div>
          )}
          
          {result.word_count && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Word Count
              </label>
              <span className="text-sm text-gray-900">{result.word_count} words</span>
            </div>
          )}
          
          {result.document_id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document ID
              </label>
              <span className="text-sm font-mono text-gray-900">{result.document_id}</span>
            </div>
          )}
        </div>
        
        {(result.details || result.raw_response) && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Classification Details
            </label>
            <StructuredClassificationDetails result={result} />
          </div>
        )}
        
        {/* Enhancement Metadata */}
        {result.enhancement_metadata && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enhancement Information
            </label>
            <div className="bg-blue-50 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-blue-900">Processing Time:</span>
                <span className="text-blue-700">{(result.enhancement_metadata.processing_time || 0).toFixed(2)}s</span>
              </div>
              
              {result.enhancement_metadata.enhancement_methods && result.enhancement_metadata.enhancement_methods.length > 0 && (
                <div className="text-xs">
                  <span className="font-medium text-blue-900">Enhancement Methods:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.enhancement_metadata.enhancement_methods.map((method, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                        {method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-xs">
                {result.enhancement_metadata.num_hypothetical_entries > 0 && (
                  <div>
                    <span className="font-medium text-blue-900">Enhanced Entries:</span>
                    <span className="ml-1 text-blue-700">{result.enhancement_metadata.num_hypothetical_entries}</span>
                  </div>
                )}
                
                {result.enhancement_metadata.num_retrieved_rules > 0 && (
                  <div>
                    <span className="font-medium text-blue-900">SCG Rules:</span>
                    <span className="ml-1 text-blue-700">{result.enhancement_metadata.num_retrieved_rules}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Retrieved Context Preview */}
        {result.retrieved_rules && result.retrieved_rules.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Retrieved SCG Rules
            </label>
            <div className="bg-green-50 rounded-md p-3 space-y-2">
              {result.retrieved_rules.slice(0, 2).map((rule, index) => (
                <div key={index} className="text-xs border-l-2 border-green-200 pl-2">
                  <div className="font-medium text-green-900">{rule.rule_id || 'Unknown Rule'}</div>
                  <div className="text-green-700">{rule.classification_level} - {rule.item || 'No description'}</div>
                  {rule.semantic_similarity && (
                    <div className="text-green-600">Similarity: {(rule.semantic_similarity * 100).toFixed(1)}%</div>
                  )}
                </div>
              ))}
              {result.retrieved_rules.length > 2 && (
                <div className="text-xs text-green-600">
                  ... and {result.retrieved_rules.length - 2} more rules
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderParagraphResults = (result) => {
    if (!result) return null;

    // If we have portion_marked_document, use the new component
    if (result.portion_marked_document || result.reasoning) {
      return (
        <div className="mt-6">
          <PortionMarkedDocument 
          paragraphResult={result} 
          formattedDocumentMutation={formattedDocumentMutation}
        />
        </div>
      );
    }

    // Fallback to legacy paragraph display
    return (
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">
            Paragraph-Level Classification Results
          </h3>
        </div>
        
        {/* Overall Document Classification */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-2">Overall Document Classification</h4>
          <div className="flex items-center space-x-4 mb-2">
            <ClassificationBadge 
              classification={result.document_classification}
              fullClassification={result.full_classification}
              caveats={result.caveats}
              releasability={result.releasability}
              confidence={result.document_confidence}
            />
            <span className="text-sm text-gray-600">
              Based on {result.total_paragraphs} paragraphs
            </span>
          </div>
          <AuthorityReviewFlag 
            reasoning={result.reasoning}
            contradictions={result.contradictions}
            variant="compact" 
          />
        </div>

        {/* Individual Paragraph Results */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Individual Paragraph Classifications</h4>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {result.paragraphs.map((paragraph, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-500">
                      Paragraph {paragraph.paragraph_number}
                    </span>
                    <ClassificationBadge 
                      classification={paragraph.classification}
                      fullClassification={paragraph.full_classification}
                      caveats={paragraph.caveats}
                      releasability={paragraph.releasability}
                      confidence={paragraph.confidence}
                      className="text-xs"
                    />
                    <AuthorityReviewFlag 
                      reasoning={paragraph.raw_response} 
                      contradictions={paragraph.contradictions}
                      variant="compact" 
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded p-3 mb-2">
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {paragraph.text}
                  </p>
                </div>
                
                {paragraph.raw_response && paragraph.raw_response !== paragraph.classification && (
                  <div className="text-xs text-gray-500">
                    <strong>Reasoning:</strong> {paragraph.raw_response}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const isLoading = isLocalLoading || classifyMutation.isLoading || paragraphMutation.isLoading || uploadMutation.isLoading;
  const currentResult = classificationResult || paragraphResult || uploadResult;

  // Debug logging
  console.log('ClassifyDocument debug:', {
    isLoading,
    isLocalLoading,
    loadingProgress,
    loadingMessage,
    classifyLoading: classifyMutation.isLoading,
    paragraphLoading: paragraphMutation.isLoading,
    uploadLoading: uploadMutation.isLoading
  });

  return (
    <div className="max-w-4xl mx-auto">
      {/* Security Shield Loader */}
      <SecurityShieldLoader 
        isLoading={isLoading} 
        progress={loadingProgress} 
        message={loadingMessage} 
      />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Legal Document Review</h1>
        <p className="mt-2 text-gray-600">
          Upload documents or enter text to analyze privilege, confidentiality, and legal review requirements
        </p>
      </div>

      {/* Results Section - Always at Top When Present */}
      {currentResult && (
        <div className="mb-8">
          {paragraphResult ? renderParagraphResults(paragraphResult) : renderResult(currentResult, uploadResult ? 'upload' : 'classification')}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('text')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'text'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Text Review
          </button>
          <button
            onClick={() => setActiveTab('file')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'file'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Document Upload
          </button>
        </nav>
      </div>

      {/* Text Classification Tab */}
      {activeTab === 'text' && (
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleTextClassification}>
            <div className="mb-4">
              <label htmlFor="text-input" className="block text-sm font-medium text-gray-700 mb-2">
                Enter text to review for privilege
              </label>
              <textarea
                id="text-input"
                rows={8}
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Paste or type the legal document content here for privilege review..."
              />
            </div>
            
            <div className="mb-4 space-y-3">
              {!useParagraphMode && (
                <div className="pl-4 border-l-2 border-blue-200 space-y-2">
                  <h4 className="text-sm font-medium text-gray-800">Enhancement Options</h4>
                  <p className="text-xs text-gray-500 mb-2">
                    These features improve classification accuracy using advanced AI algorithms
                  </p>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={useEnhancement}
                      onChange={(e) => setUseEnhancement(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Enable advanced retrieval enhancement
                    </span>
                  </label>
                  <p className="ml-6 text-xs text-gray-500">
                    Uses advanced algorithms to improve legal rule matching accuracy
                  </p>
                </div>
              )}
              
              {/* Document Segmentation Options */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-800">Document Segmentation</h4>
                <p className="text-xs text-gray-500 mb-2">
                  Choose how to break down documents for individual paragraph classification
                </p>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useParagraphMode}
                    onChange={(e) => setUseParagraphMode(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Segment Document/Paragraphs with Simple Regex (Faster, less original formatting maintained)
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useLlmSegmentation}
                    onChange={(e) => setUseLlmSegmentation(e.target.checked)}
                    className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Segment Every Portion of the document Using AI (Bullet Points, Paragraphs, etc)
                  </span>
                </label>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={!textInput.trim() || classifyMutation.isLoading || paragraphMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {(classifyMutation.isLoading || paragraphMutation.isLoading) ? (
                <>
                  <ClockIcon className="w-4 h-4 mr-2 animate-spin" />
                  {useParagraphMode ? 'Classifying Paragraphs...' : 'Classifying...'}
                </>
              ) : (
                <>
                  <DocumentIcon className="w-4 h-4 mr-2" />
                  {useParagraphMode ? 'Review Paragraphs' : 'Review Document'}
                </>
              )}
            </button>
          </form>
          
          {(classifyMutation.isError || paragraphMutation.isError) && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    Legal review failed: {
                      classifyMutation.error?.response?.data?.detail || 
                      paragraphMutation.error?.response?.data?.detail || 
                      'Unknown error'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Results moved to top - removed from here */}
        </div>
      )}

      {/* File Upload Tab */}
      {activeTab === 'file' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Document</h3>
            <p className="text-sm text-gray-600">
              Upload PDF, TXT, JSON, or DOCX files. The system will extract text and analyze the document for privilege, confidentiality, and legal review requirements.
            </p>
          </div>
          
          <div className="mb-4 space-y-3">
            {/* Document Segmentation Options for File Upload */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-800">Document Segmentation</h4>
              <p className="text-xs text-gray-500 mb-2">
                Choose how to break down documents for individual paragraph classification
              </p>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useParagraphMode}
                  onChange={(e) => setUseParagraphMode(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Segment Document/Paragraphs with Simple Regex (Faster, less original formatting maintained)
                </span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useLlmSegmentation}
                  onChange={(e) => setUseLlmSegmentation(e.target.checked)}
                  className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Segment Every Portion of the document Using AI (Bullet Points, Paragraphs, etc)
                </span>
              </label>
            </div>
          </div>
          
          <FileUpload 
            onFileSelect={handleFileUpload}
            accept=".pdf,.txt,.json,.docx"
            className="mb-4"
          />
          
          {uploadMutation.isLoading && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <ClockIcon className="w-5 h-5 text-blue-400 animate-spin mr-3" />
                <p className="text-sm text-blue-700">Processing document...</p>
              </div>
            </div>
          )}
          
          {uploadMutation.isError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    Upload failed: {uploadMutation.error?.response?.data?.detail || 'Unknown error'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Results moved to top - removed from here */}
        </div>
      )}
    </div>
  );
}